import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useRouter } from "vue-router"; // Import useRouter
import {
  isSubscribed,
  getUserWorkspaces,
  createCustomerPortalSession,
  clearUserWorkspacesCache,
  hasUserExceededFileUploadLimit,
  getAudioFile,
  getAudioFiles,
  getAudioFilesPage,
  updateAudioFile,
  getWorkspaceTags,
  attachTag,
  detachTag,
} from "@/services/workspace.service";
import { clearCache } from "@/services/external-api.service";

export const useWorkSpaceStore = defineStore("workspace", () => {
  const router = useRouter(); // Initialize router
  const activeWorkspaceUUID = ref("");
  const workspaces = ref([]); // Store fetched workspaces
  const workspacesLoaded = ref(false); // Track if workspaces have been loaded
  const audioFiles = ref({}); // Map of workspaceUUID -> Map of audioFileUUID -> audioFile
  const audioFilesLoaded = ref({}); // Track if audio files have been loaded for a workspace
  const activeAudioFile = ref(null);
  const playerCurrentTimeInMilliseconds = ref(0);
  const updatedPlayerTimeInMilliseconds = ref(0);
  const chatToken = ref({});
  const openPopupId = ref(null);
  const fileUploaded = ref(false);
  const fileDeleted = ref(false);
  const isSubscribedState = ref(false);
  const subscriptionLoaded = ref(false);
  // True only when the LAST fetchSubscriptionStatus call actually resolved
  // entitlement (subscriptionLoaded flips even on failure so UIs don't spin).
  const subscriptionStatusSucceeded = ref(false);
  const subscription = ref(null);
  // Which provider backs the active subscription: "stripe" | "apple"
  // | "google_play" | "both" | "none" | null. Comes from the is-subscribed
  // EntitlementResult. Drives provider-aware billing UX (App Store / Play
  // Store subs aren't managed via Stripe).
  const subscriptionSource = ref(null);
  // Which providers grant it — { stripe, apple, google }, each non-null only
  // when that provider grants access (the entitlement's `sources`). Lets the
  // Billing page say where the user subscribed, including for "both".
  const subscriptionSources = ref(null);
  const billingPortalUrl = ref(null);
  const billingPortalLoaded = ref(false);

  // Audio file caching
  const audioFileCache = ref({}); // Cache for individual audio files
  const audioFileCacheLastUpdated = ref({}); // Track when each audio file was last fetched
  const AUDIO_FILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Workspace tag list, keyed by workspaceUUID (for the tag picker)
  const workspaceTags = ref({});

  // Paginated conversation feed, keyed by scope (workspaceUUID, or
  // `${workspaceUUID}:fav` for favorites). Entity data lives in audioFiles;
  // each feed holds only the ordered UUIDs + cursor state for infinite scroll.
  const conversationFeeds = ref({});
  // Active conversation filters per workspace:
  // { tags: [{tagUUID,name,color}], datePreset, dateLabel, createdFrom, createdTo }
  const conversationFilters = ref({});
  const CONVERSATION_PAGE_SIZE = 30;

  // Daily file limit tracking
  const hasExceededFileUploadLimit = ref(false);
  const remainingFiles = ref(0);
  const dailyFileCount = ref(0);
  const dailyFileLimitLoaded = ref(false);
  const dailyFileLimitLastChecked = ref(0);
  const DAILY_LIMIT_CACHE_TTL = 2 * 60 * 1000; // 2 minutes in milliseconds

  // Centralized computed property to indicate if the subscription is yearly
  const isYearly = computed(() => {
    return subscription.value && subscription.value.billingCycle === "YEAR";
  });

  // Bumped by every status fetch, so an answer older than one already
  // applied can't overwrite it (the app-level fetch overlaps the Billing
  // page's and the promo redirect's own refreshes).
  let subscriptionRequest = 0;

  async function fetchSubscriptionStatus() {
    const mine = ++subscriptionRequest;
    // Whether THIS call resolved entitlement. Flows with double-charge risk
    // (the promo checkout redirect) fail CLOSED on false, so a failed call
    // must never report another call's success.
    let succeeded = false;
    subscriptionStatusSucceeded.value = false;
    try {
      const response = await isSubscribed();
      // The API helper resolves HTTP failures as { error }. Stop before any
      // assignment: a failure must not turn a paying user into a free one.
      if (response?.error || !response?.data) {
        throw new Error(response?.error?.message || "No subscription status");
      }
      // A newer fetch has answered already: its state wins, and this call
      // can't vouch for what's stored now, so it reports failure. Callers
      // with double-charge risk (the promo redirect) fail closed.
      if (mine !== subscriptionRequest) return false;
      succeeded = true;
      isSubscribedState.value = response.data.isSubscribed;
      // Store only the subscription object from the response
      subscription.value = response.data.subscription;
      // Provider backing the sub ("stripe" | "apple" | "both" | "none").
      subscriptionSource.value = response.data.source ?? null;
      subscriptionSources.value = response.data.sources ?? null;
      // Entitlement is resolved at this point — record it BEFORE the portal
      // call below, whose failure must not make callers treat entitlement as
      // unknown. Flows with double-charge risk (e.g. the promo checkout
      // redirect) fail CLOSED when this is false.
      subscriptionStatusSucceeded.value = true;

      // Apple (App Store) and Google Play subscriptions are managed in their
      // stores — the Stripe portal endpoint 400s for them — so skip the
      // portal fetch for them. Everything else is treated as Stripe-capable
      // (fail OPEN): a real Stripe subscriber must never be denied their
      // portal by an unexpected source value (a partial deploy, proxy quirk,
      // or a future provider string), so we only hard-exclude the sources we
      // KNOW have no Stripe customer: "apple", "google_play", and "none".
      // null/undefined preserves the pre-change behavior of attempting the
      // portal.
      // When the API lists the providers, use that (it resolves "both");
      // "comp" (a plan AudioScribe granted) has no Stripe customer either.
      const source = subscriptionSource.value;
      const sources = subscriptionSources.value;
      const hasStripeBilling =
        response.data.isSubscribed &&
        (sources
          ? Boolean(sources.stripe)
          : source !== "apple" &&
            source !== "google_play" &&
            source !== "none" &&
            source !== "comp");
      // The old link is a session that has expired: drop it before asking
      // for a new one, so a failure can't leave a stale link on screen.
      billingPortalUrl.value = null;
      if (hasStripeBilling) {
        const portalResponse = await createCustomerPortalSession();
        // A newer fetch may have answered while this one waited: its link,
        // or the absence of one, wins.
        if (mine !== subscriptionRequest) return false;
        // A failed portal call leaves no link rather than a stale one.
        billingPortalUrl.value =
          portalResponse?.data?.customerPortalUrl ?? null;
      }
    } catch (error) {
      console.error("Failed to fetch subscription status", error);
    } finally {
      if (mine === subscriptionRequest) {
        subscriptionLoaded.value = true;
        billingPortalLoaded.value = true;
      }
    }
    // A call superseded while it waited — including one whose portal
    // request threw, which skips the check above — can't vouch for what's
    // stored now, so it reports failure and its caller fails closed.
    return succeeded && mine === subscriptionRequest;
  }

  // Bumped by every workspace change the server has confirmed. A list request
  // that was in flight across one answers with older data.
  let workspacesRevision = 0;

  // Fetch workspaces from backend and determine the active workspace
  async function fetchWorkspaces() {
    const revision = workspacesRevision;
    try {
      const response = await getUserWorkspaces();
      // The API helper resolves HTTP failures as { error } rather than
      // throwing. Keep the last good list: a null here breaks every reader
      // (canCreateWorkspace, activeWorkspace, the switchers).
      if (response?.error || !Array.isArray(response?.data)) {
        throw new Error(
          response?.error?.message || "No workspaces in the response",
        );
      }
      // A change confirmed while this request was out is newer than its
      // answer: keep what the change produced.
      if (revision !== workspacesRevision) return;
      workspaces.value = response.data;

      // Check localStorage for saved workspace UUID
      const savedWorkspaceUUID = localStorage.getItem(
        "currentSelectedWorkspaceUUID",
      );

      // Validate the saved workspace UUID
      if (
        savedWorkspaceUUID &&
        workspaces.value.some((ws) => ws.workspaceUUID === savedWorkspaceUUID)
      ) {
        activeWorkspaceUUID.value = savedWorkspaceUUID;
      } else if (workspaces.value.length > 0) {
        // Default to the first workspace if no valid saved UUID
        activeWorkspaceUUID.value = workspaces.value[0].workspaceUUID;
        saveActiveWorkspace(activeWorkspaceUUID.value);
      }
      workspacesLoaded.value = true; // Mark workspaces as loaded
    } catch (error) {
      console.error("Failed to fetch workspaces:", error);
    }
  }

  function noteConfirmedChange() {
    workspacesRevision += 1;
    // Otherwise the refetch that follows is served the cached old list.
    clearUserWorkspacesCache();
  }

  // Apply a workspace change the server has already confirmed. Callers still
  // refetch, but neither a failed refetch nor an answer from before the
  // change may leave the switcher, the create limit or the active workspace
  // stale.
  function addWorkspace(workspace) {
    noteConfirmedChange();
    const known = workspaces.value.some(
      (ws) => ws.workspaceUUID === workspace.workspaceUUID,
    );
    // A copy, so a caller editing its own object doesn't rename the store's.
    if (!known) workspaces.value = [...workspaces.value, { ...workspace }];
  }

  function renameWorkspace(workspaceUUID, name) {
    noteConfirmedChange();
    const workspace = workspaces.value.find(
      (ws) => ws.workspaceUUID === workspaceUUID,
    );
    if (workspace) workspace.name = name;
  }

  function removeWorkspace(workspaceUUID) {
    noteConfirmedChange();
    workspaces.value = workspaces.value.filter(
      (ws) => ws.workspaceUUID !== workspaceUUID,
    );
    // The same fallback fetchWorkspaces() applies to a saved workspace that
    // no longer exists.
    if (
      activeWorkspaceUUID.value === workspaceUUID &&
      workspaces.value.length > 0
    ) {
      activeWorkspaceUUID.value = workspaces.value[0].workspaceUUID;
      saveActiveWorkspace(activeWorkspaceUUID.value);
    }
  }

  // Save the active workspace UUID in localStorage
  function saveActiveWorkspace(uuid) {
    localStorage.setItem("currentSelectedWorkspaceUUID", uuid);
  }

  // Update active workspace and save to localStorage
  function setActiveWorkspace(uuid) {
    if (workspaces.value.some((ws) => ws.workspaceUUID === uuid)) {
      activeWorkspaceUUID.value = uuid;
      saveActiveWorkspace(uuid);
      // Only push route if router is available (not in test environment)
      if (router) {
        router.push({
          name: "workspace-home",
          params: { workspaceUUID: uuid },
        });
      }
    }
  }

  function toggleFileUploaded() {
    fileUploaded.value = !fileUploaded.value;
  }

  function setActiveWorkspaceUUID(workspaceUUID) {
    activeWorkspaceUUID.value = workspaceUUID;
  }

  function setOpenPopupId(id) {
    openPopupId.value = id;
  }

  // Workspace plan policy: free = 1 workspace, paid = 3. Single source of
  // truth for the desktop switcher, settings page, and mobile sheet.
  const workspaceLimit = computed(() => (isSubscribedState.value ? 3 : 1));
  const canCreateWorkspace = computed(
    () => workspaces.value.length < workspaceLimit.value,
  );

  const activeWorkspace = computed(() =>
    workspaces.value.find(
      (ws) => ws.workspaceUUID === activeWorkspaceUUID.value,
    ),
  );
  const activeWorkspaceName = computed(() => activeWorkspace.value?.name);

  // Method to check the daily file upload limit
  async function checkDailyFileUploadLimit(force = false) {
    // Skip check if user is subscribed (unlimited uploads)
    if (isSubscribedState.value) {
      hasExceededFileUploadLimit.value = false;
      remainingFiles.value = Infinity;
      dailyFileLimitLoaded.value = true;
      return {
        hasExceededLimit: false,
        remainingFiles: Infinity,
      };
    }

    const now = Date.now();

    // Return cached data if not forced and within TTL
    if (
      !force &&
      dailyFileLimitLoaded.value &&
      now - dailyFileLimitLastChecked.value < DAILY_LIMIT_CACHE_TTL
    ) {
      console.log("[Store] Using cached daily file limit data");
      return {
        hasExceededLimit: hasExceededFileUploadLimit.value,
        remainingFiles: remainingFiles.value,
      };
    }

    try {
      console.log("[Store] Fetching fresh daily file limit data");
      const response = await hasUserExceededFileUploadLimit(
        activeWorkspaceUUID.value,
      );

      if (response.data) {
        hasExceededFileUploadLimit.value = response.data.hasExceededLimit;
        dailyFileCount.value = response.data.count;
        remainingFiles.value = Math.max(0, response.data.remainingFiles);
      }

      dailyFileLimitLoaded.value = true;
      dailyFileLimitLastChecked.value = now;

      return {
        hasExceededLimit: hasExceededFileUploadLimit.value,
        remainingFiles: remainingFiles.value,
      };
    } catch (error) {
      console.error("Failed to check daily file upload limit", error);
      return {
        hasExceededLimit: hasExceededFileUploadLimit.value,
        remainingFiles: remainingFiles.value,
      };
    }
  }

  // Method to invalidate daily file limit cache after a file action
  function invalidateDailyFileLimit() {
    dailyFileLimitLoaded.value = false;
    clearCache("daily-file-limit");
  }

  // Method to fetch all audio files for a workspace
  async function fetchAudioFiles(
    workspaceUUID = activeWorkspaceUUID.value,
    force = false,
  ) {
    // If workspaceUUID is not provided or empty, use the active workspace
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;

    if (!wsUUID) {
      console.error(
        "[Store] Cannot fetch audio files: No workspace UUID provided",
      );
      return { data: [] };
    }

    console.log(
      `[Store] fetchAudioFiles called - force: ${force}, audioFilesLoaded[${wsUUID}]: ${audioFilesLoaded.value[wsUUID]}`,
    );
    console.log(
      `[Store] Current cached files:`,
      audioFiles.value[wsUUID]
        ? Object.keys(audioFiles.value[wsUUID]).length
        : 0,
    );

    // Return cached data if not forced and already loaded
    if (!force && audioFilesLoaded.value[wsUUID]) {
      console.log(`[Store] Using cached audio files for workspace ${wsUUID}`);
      const cachedData = audioFiles.value[wsUUID] || {};
      console.log(
        `[Store] Returning cached data with ${
          Object.keys(cachedData).length
        } files`,
      );
      return { data: cachedData };
    }

    try {
      console.log(
        `[Store] Fetching fresh audio files for workspace ${wsUUID}`,
        force ? "(bypassing cache)" : "",
      );
      const response = await getAudioFiles(wsUUID, force); // Pass force as skipCache parameter

      if (response.data) {
        console.log(`[Store] API returned ${response.data.length} files`);

        // Initialize the workspace entry if it doesn't exist
        if (!audioFiles.value[wsUUID]) {
          audioFiles.value[wsUUID] = {};
        }

        // Index the audio files by UUID for quick access
        const audioFilesMap = {};
        response.data.forEach((file) => {
          audioFilesMap[file.audioFileUUID] = file;
          // console.log(
          //   `[Store] File: ${file.audioFileUUID} - Name: ${file.userDefinedName}`,
          // );
        });

        // Update the store
        audioFiles.value[wsUUID] = audioFilesMap;
        audioFilesLoaded.value[wsUUID] = true;
      }

      return response;
    } catch (error) {
      console.error(
        `[Store] Failed to fetch audio files for workspace ${wsUUID}`,
        error,
      );
      return { data: [], error };
    }
  }

  // Method to fetch a single audio file by UUID
  async function fetchAudioFile(
    audioFileUUID,
    workspaceUUID = activeWorkspaceUUID.value,
    force = false,
  ) {
    // If workspaceUUID is not provided or empty, use the active workspace
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;

    if (!wsUUID || !audioFileUUID) {
      console.error(
        "[Store] Cannot fetch audio file: Missing workspace UUID or audio file UUID",
      );
      return { data: null, error: "Missing parameters" };
    }

    const cacheKey = `${wsUUID}:${audioFileUUID}`;
    const now = Date.now();

    // Return cached data if not forced and within TTL
    if (
      !force &&
      audioFileCache.value[cacheKey] &&
      now - (audioFileCacheLastUpdated.value[cacheKey] || 0) <
        AUDIO_FILE_CACHE_TTL
    ) {
      console.log(`[Store] Using cached audio file ${audioFileUUID}`);

      // Update activeAudioFile if this is the requested audio file
      activeAudioFile.value = audioFileCache.value[cacheKey];

      return { data: audioFileCache.value[cacheKey] };
    }

    try {
      console.log(`[Store] Fetching fresh audio file ${audioFileUUID}`);
      const response = await getAudioFile(wsUUID, audioFileUUID);

      if (response.data) {
        // Cache the audio file
        audioFileCache.value[cacheKey] = response.data;
        audioFileCacheLastUpdated.value[cacheKey] = now;

        // Update activeAudioFile
        activeAudioFile.value = response.data;

        // Also update the audio files collection if it exists
        if (audioFiles.value[wsUUID]) {
          audioFiles.value[wsUUID][audioFileUUID] = response.data;
        }
      }

      return response;
    } catch (error) {
      console.error(
        `[Store] Failed to fetch audio file ${audioFileUUID}`,
        error,
      );
      return { data: null, error };
    }
  }

  // Method to clear audio file cache when files are deleted/updated/renamed
  // IMPORTANT: This must be called after any operation that modifies an audio file (rename, delete, etc.)
  // to ensure all views (transcription, list view, etc.) remain in sync
  function invalidateAudioFileCache(
    audioFileUUID = null,
    workspaceUUID = activeWorkspaceUUID.value,
  ) {
    console.log(
      `[Store] invalidateAudioFileCache called - file: ${audioFileUUID}, workspace: ${workspaceUUID}`,
    );

    if (audioFileUUID) {
      // Clear a specific audio file cache
      const cacheKey = `${workspaceUUID}:${audioFileUUID}`;
      console.log(`[Store] Deleting cache key: ${cacheKey}`);
      delete audioFileCache.value[cacheKey];
      delete audioFileCacheLastUpdated.value[cacheKey];

      // Also mark audioFiles collection as not loaded so it will be refreshed next time
      // This ensures the ConversationsList will get the updated data
      console.log(`[Store] Setting audioFilesLoaded[${workspaceUUID}] = false`);
      console.log(
        `[Store] Before: audioFilesLoaded state:`,
        audioFilesLoaded.value,
      );
      // Trigger reactivity by reassigning the whole object
      audioFilesLoaded.value = {
        ...audioFilesLoaded.value,
        [workspaceUUID]: false,
      };
      console.log(
        `[Store] After: audioFilesLoaded state:`,
        audioFilesLoaded.value,
      );

      // Clear the API cache for this specific file
      console.log(
        `[Store] Clearing API cache for: audio-file/${audioFileUUID}`,
      );
      clearCache(`audio-file/${audioFileUUID}`);

      // ALSO clear the list endpoint cache since it contains this file
      console.log(
        `[Store] Clearing API cache for list endpoint: workspace/${workspaceUUID}/audio-file`,
      );
      clearCache(`workspace/${workspaceUUID}/audio-file`);

      // Debug: Check if we should also clear the list cache
      console.log(
        `[Store] Current audioFiles[${workspaceUUID}]:`,
        audioFiles.value[workspaceUUID]
          ? Object.keys(audioFiles.value[workspaceUUID]).length + " files"
          : "not loaded",
      );
    } else {
      // Clear all audio file caches for the specified workspace
      Object.keys(audioFileCache.value).forEach((key) => {
        if (key.startsWith(`${workspaceUUID}:`)) {
          delete audioFileCache.value[key];
          delete audioFileCacheLastUpdated.value[key];
        }
      });

      // Clear the audioFiles collection for this workspace
      if (audioFiles.value[workspaceUUID]) {
        audioFiles.value[workspaceUUID] = {};
      }

      // Mark as not loaded so it will be refreshed next time
      // Trigger reactivity by reassigning the whole object
      audioFilesLoaded.value = {
        ...audioFilesLoaded.value,
        [workspaceUUID]: false,
      };

      // Clear the API cache for all audio files in this workspace
      clearCache(`workspace/${workspaceUUID}/audio-file`);
    }
  }

  // Method to toggle favorite status for an audio file
  async function toggleFavorite(
    audioFileUUID,
    workspaceUUID = activeWorkspaceUUID.value,
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;

    if (!wsUUID || !audioFileUUID) {
      console.error("[Store] Cannot toggle favorite: Missing parameters");
      return { error: "Missing parameters" };
    }

    // Get current favorite state from cache
    const currentFile = audioFiles.value[wsUUID]?.[audioFileUUID];
    const newFavoriteState = !currentFile?.isFavorite;

    console.log(
      `[Store] Toggling favorite for ${audioFileUUID} to ${newFavoriteState}`,
    );

    // Truly optimistic: apply the new state to every local cache FIRST so
    // the heart responds instantly, then confirm with the API and roll
    // back on failure. (The previous version awaited the round-trip
    // before updating, which read as a broken button on slow links.)
    const applyLocalState = (state) => {
      if (audioFiles.value[wsUUID] && audioFiles.value[wsUUID][audioFileUUID]) {
        audioFiles.value[wsUUID][audioFileUUID].isFavorite = state;
      }
      const cacheKey = `${wsUUID}:${audioFileUUID}`;
      if (audioFileCache.value[cacheKey]) {
        audioFileCache.value[cacheKey].isFavorite = state;
      }
      // Keep the favorites feed in sync: drop the file when unfavorited,
      // surface it (newest-first) when favorited.
      const favFeed = conversationFeeds.value[feedKey(wsUUID, true)];
      if (favFeed) {
        if (state) {
          if (!favFeed.order.includes(audioFileUUID)) {
            favFeed.order = [audioFileUUID, ...favFeed.order];
          }
        } else {
          favFeed.order = favFeed.order.filter((u) => u !== audioFileUUID);
        }
      }
    };

    applyLocalState(newFavoriteState);

    try {
      const response = await updateAudioFile(wsUUID, audioFileUUID, {
        isFavorite: newFavoriteState,
      });

      if (!response.data) {
        // Server rejected the change — roll the UI back.
        applyLocalState(!newFavoriteState);
      }

      return response;
    } catch (error) {
      console.error(
        `[Store] Failed to toggle favorite for ${audioFileUUID}`,
        error,
      );
      applyLocalState(!newFavoriteState);
      return { error };
    }
  }

  // Method to get only favorite audio files for a workspace
  /**
   * Rename a file and keep the caches honest. Shared by the desktop
   * MediaTitle editor and the mobile rename dialog; toasts and local
   * component state stay with the callers.
   *
   * updateAudioFile resolves an HTTP failure as { data: null, error } rather
   * than throwing, so the result has to be checked. On failure nothing local
   * changes (the caches keep the old name) and the error goes back to the
   * caller.
   *
   * @returns {Promise<{data: string} | {data: null, error: object|string} | {error: string}>}
   *   { data: name } on success; { data: null, error } if the rename fails;
   *   { error } for missing parameters.
   */
  async function renameAudioFile(
    audioFileUUID,
    userDefinedName,
    workspaceUUID = activeWorkspaceUUID.value,
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    if (!wsUUID || !audioFileUUID || !userDefinedName) {
      return { error: "Missing parameters" };
    }
    try {
      const response = await updateAudioFile(wsUUID, audioFileUUID, {
        userDefinedName,
      });
      if (response?.error) {
        console.error("[Store] Failed to rename audio file:", response.error);
        return { data: null, error: response.error };
      }
      invalidateAudioFileCache(audioFileUUID);
      return { data: userDefinedName };
    } catch (error) {
      console.error("[Store] Failed to rename audio file:", error);
      return { data: null, error };
    }
  }

  function getFavoriteAudioFiles(workspaceUUID = activeWorkspaceUUID.value) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    const wsFiles = audioFiles.value[wsUUID] || {};
    return Object.values(wsFiles).filter((file) => file.isFavorite);
  }

  // ---------------------------------------------------------------------------
  // Tags
  // ---------------------------------------------------------------------------

  // Fetch (and cache) the workspace's tag list for the picker.
  async function fetchWorkspaceTags(
    workspaceUUID = activeWorkspaceUUID.value,
    force = false,
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    if (!wsUUID) return [];

    if (!force && workspaceTags.value[wsUUID]) {
      return workspaceTags.value[wsUUID];
    }

    try {
      const response = await getWorkspaceTags(wsUUID);
      if (response.data) {
        workspaceTags.value[wsUUID] = response.data;
      }
      return workspaceTags.value[wsUUID] || [];
    } catch (error) {
      console.error(
        `[Store] Failed to fetch tags for workspace ${wsUUID}`,
        error,
      );
      return workspaceTags.value[wsUUID] || [];
    }
  }

  // Keep all cached copies of a file's tags (list + detail + active) in sync
  // after a tag change.
  function applyFileTags(wsUUID, audioFileUUID, tags) {
    if (audioFiles.value[wsUUID]?.[audioFileUUID]) {
      audioFiles.value[wsUUID][audioFileUUID].tags = tags;
    }
    const cacheKey = `${wsUUID}:${audioFileUUID}`;
    if (audioFileCache.value[cacheKey]) {
      audioFileCache.value[cacheKey].tags = tags;
    }
    if (activeAudioFile.value?.audioFileUUID === audioFileUUID) {
      activeAudioFile.value.tags = tags;
    }
  }

  // Attach a tag by { tagUUID } (existing) or { name } (create-if-missing).
  // Returns { data: <file's full tag list> } or { error }.
  async function attachTagToFile(
    audioFileUUID,
    payload,
    workspaceUUID = activeWorkspaceUUID.value,
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    if (!wsUUID || !audioFileUUID) return { error: "Missing parameters" };

    try {
      const response = await attachTag(wsUUID, audioFileUUID, payload);
      if (response.data) {
        applyFileTags(wsUUID, audioFileUUID, response.data);
        // A new tag may have been created — invalidate the cached tag list.
        if (payload?.name) {
          workspaceTags.value[wsUUID] = null;
        }
      }
      return response;
    } catch (error) {
      console.error(`[Store] Failed to attach tag to ${audioFileUUID}`, error);
      return { error };
    }
  }

  // Detach a tag from a file. Returns { data: <remaining tags> } or { error }.
  async function detachTagFromFile(
    audioFileUUID,
    tagUUID,
    workspaceUUID = activeWorkspaceUUID.value,
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    if (!wsUUID || !audioFileUUID) return { error: "Missing parameters" };

    try {
      const response = await detachTag(wsUUID, audioFileUUID, tagUUID);
      if (response.data) {
        applyFileTags(wsUUID, audioFileUUID, response.data);
      }
      return response;
    } catch (error) {
      console.error(
        `[Store] Failed to detach tag from ${audioFileUUID}`,
        error,
      );
      return { error };
    }
  }

  // ---------------------------------------------------------------------------
  // Conversation feed (keyset pagination / infinite scroll)
  // ---------------------------------------------------------------------------

  function feedKey(wsUUID, favorite) {
    return favorite ? `${wsUUID}:fav` : wsUUID;
  }

  // Active server-side filter params for a feed scope. Filters apply only to
  // the main conversation feed, never to favorites.
  function filterParams(wsUUID, favorite) {
    if (favorite) return {};
    const f = conversationFilters.value[wsUUID];
    if (!f) return {};
    return {
      tagUUIDs: (f.tags || []).map((t) => t.tagUUID),
      createdFrom: f.createdFrom || null,
      createdTo: f.createdTo || null,
    };
  }

  // Fresh feed state. `error` holds the last failed page load as
  // { message, status, page: "first" | "next" } until a later load clears it,
  // so a list can offer a retry instead of claiming the workspace is empty.
  function emptyFeed() {
    return {
      order: [],
      cursor: null,
      hasNextPage: true,
      loading: false,
      initialized: false,
      error: null,
    };
  }

  // getAudioFilesPage resolves an HTTP failure as { data: null, error } and
  // throws only when it can't get a token. Normalise both for the feed.
  function toFeedError(error, page) {
    return {
      message:
        (typeof error === "string" ? error : error?.message) ||
        "Failed to load conversations",
      status: error?.status ?? null,
      page,
    };
  }

  function ensureFeed(key) {
    if (!conversationFeeds.value[key]) {
      conversationFeeds.value[key] = emptyFeed();
    }
    return conversationFeeds.value[key];
  }

  function upsertAudioFileEntity(wsUUID, file) {
    if (!audioFiles.value[wsUUID]) audioFiles.value[wsUUID] = {};
    audioFiles.value[wsUUID][file.audioFileUUID] = {
      ...(audioFiles.value[wsUUID][file.audioFileUUID] || {}),
      ...file,
    };
  }

  // Read-only view of a feed: resolves ordered UUIDs through the entity map so
  // per-file updates (favorite, tags, rename) reflect live.
  function getConversationFeed(
    workspaceUUID = activeWorkspaceUUID.value,
    { favorite = false } = {},
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    const feed = conversationFeeds.value[feedKey(wsUUID, favorite)];
    if (!feed) {
      return {
        items: [],
        hasNextPage: true,
        loading: false,
        initialized: false,
        error: null,
      };
    }
    const map = audioFiles.value[wsUUID] || {};
    return {
      items: feed.order.map((uuid) => map[uuid]).filter(Boolean),
      hasNextPage: feed.hasNextPage,
      loading: feed.loading,
      initialized: feed.initialized,
      error: feed.error || null,
    };
  }

  async function loadConversationsFirstPage(
    workspaceUUID = activeWorkspaceUUID.value,
    { favorite = false } = {},
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    if (!wsUUID) return { error: "Missing workspace" };
    const feed = ensureFeed(feedKey(wsUUID, favorite));
    feed.loading = true;
    try {
      const res = await getAudioFilesPage(wsUUID, {
        limit: CONVERSATION_PAGE_SIZE,
        isFavorite: favorite,
        ...filterParams(wsUUID, favorite),
      });
      if (res?.error) {
        // A failed page is not an empty workspace: keep whatever was loaded
        // and leave `initialized` alone so the list can offer a retry.
        feed.error = toFeedError(res.error, "first");
        return { data: null, error: res.error };
      }
      const page = res?.data || {};
      const items = page.items || [];
      items.forEach((f) => upsertAudioFileEntity(wsUUID, f));
      feed.order = items.map((f) => f.audioFileUUID);
      feed.cursor = page.pageInfo?.nextCursor ?? null;
      feed.hasNextPage = page.pageInfo?.hasNextPage ?? false;
      feed.initialized = true;
      feed.error = null;
      return { data: items };
    } catch (error) {
      console.error("[Store] loadConversationsFirstPage failed", error);
      feed.error = toFeedError(error, "first");
      return { data: null, error };
    } finally {
      feed.loading = false;
    }
  }

  async function loadConversationsNextPage(
    workspaceUUID = activeWorkspaceUUID.value,
    { favorite = false } = {},
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    const feed = ensureFeed(feedKey(wsUUID, favorite));
    if (feed.loading || !feed.hasNextPage || !feed.initialized) {
      return { data: [] };
    }
    feed.loading = true;
    try {
      const res = await getAudioFilesPage(wsUUID, {
        limit: CONVERSATION_PAGE_SIZE,
        cursor: feed.cursor,
        isFavorite: favorite,
        ...filterParams(wsUUID, favorite),
      });
      if (res?.error) {
        // Keep the cursor and hasNextPage so this page can be retried.
        // Clearing hasNextPage here used to stop infinite scroll for good.
        feed.error = toFeedError(res.error, "next");
        return { data: null, error: res.error };
      }
      const page = res?.data || {};
      const items = page.items || [];
      const seen = new Set(feed.order);
      items.forEach((f) => {
        upsertAudioFileEntity(wsUUID, f);
        if (!seen.has(f.audioFileUUID)) feed.order.push(f.audioFileUUID);
      });
      feed.cursor = page.pageInfo?.nextCursor ?? null;
      feed.hasNextPage = page.pageInfo?.hasNextPage ?? false;
      feed.error = null;
      return { data: items };
    } catch (error) {
      console.error("[Store] loadConversationsNextPage failed", error);
      feed.error = toFeedError(error, "next");
      return { data: null, error };
    } finally {
      feed.loading = false;
    }
  }

  // Re-fetch the first page: update existing entities in place and prepend any
  // newly-created files (newest first). Used by the poll + after upload. Does
  // not disturb already-loaded deeper pages.
  async function refreshConversationsFirstPage(
    workspaceUUID = activeWorkspaceUUID.value,
    { favorite = false } = {},
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    const feed = conversationFeeds.value[feedKey(wsUUID, favorite)];
    if (!feed || !feed.initialized) {
      return loadConversationsFirstPage(wsUUID, { favorite });
    }
    try {
      const res = await getAudioFilesPage(wsUUID, {
        limit: CONVERSATION_PAGE_SIZE,
        isFavorite: favorite,
        ...filterParams(wsUUID, favorite),
      });
      if (res?.error) {
        // Background poll: keep the list as it is and try again next time.
        return { data: null, error: res.error };
      }
      const page = res?.data || {};
      const items = page.items || [];
      const seen = new Set(feed.order);
      // Genuinely-new files (e.g. a fresh upload) always sort to the front of
      // the newest-first page, so they form a leading PREFIX of brand-new
      // UUIDs. Collect only that prefix and stop at the first already-loaded
      // file. Anything after it is either already in the feed or an OLDER file
      // that merely surfaced into the newest-page window because something
      // ahead of it was deleted — that backfill belongs at the bottom (it
      // loads on scroll), never prepended to the top. (Without this guard, the
      // 10s poll after a delete hoists the newly-exposed older file to the top
      // of the list, shoving the visible rows downward.)
      const fresh = [];
      let hitKnown = false;
      items.forEach((f) => {
        // Always refresh the entity so in-place updates (rename, status,
        // preview URLs) for already-loaded files still take effect.
        upsertAudioFileEntity(wsUUID, f);
        if (hitKnown) return;
        if (seen.has(f.audioFileUUID)) {
          hitKnown = true;
        } else {
          fresh.push(f.audioFileUUID);
        }
      });
      if (fresh.length) feed.order = [...fresh, ...feed.order];
      // A good first page supersedes an earlier failed reload of it.
      if (feed.error?.page === "first") feed.error = null;
      return { data: items };
    } catch (error) {
      console.error("[Store] refreshConversationsFirstPage failed", error);
      return { error };
    }
  }

  function getConversationFilters(workspaceUUID = activeWorkspaceUUID.value) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    return (
      conversationFilters.value[wsUUID] || {
        tags: [],
        datePreset: "any",
        dateLabel: "",
        createdFrom: null,
        createdTo: null,
      }
    );
  }

  // Set conversation filters for a workspace, reset the main feed, reload page 1.
  async function setConversationFilters(
    workspaceUUID = activeWorkspaceUUID.value,
    filters = {},
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    if (!wsUUID) return { error: "Missing workspace" };
    conversationFilters.value[wsUUID] = {
      tags: filters.tags || [],
      datePreset: filters.datePreset || "any",
      dateLabel: filters.dateLabel || "",
      createdFrom: filters.createdFrom || null,
      createdTo: filters.createdTo || null,
    };
    // Reset the main feed so pagination restarts cleanly under the new filters.
    conversationFeeds.value[feedKey(wsUUID, false)] = emptyFeed();
    return loadConversationsFirstPage(wsUUID);
  }

  // Remove a file from all feeds + the entity map (called on delete).
  function removeAudioFileFromFeeds(
    audioFileUUID,
    workspaceUUID = activeWorkspaceUUID.value,
  ) {
    const wsUUID = workspaceUUID || activeWorkspaceUUID.value;
    [feedKey(wsUUID, false), feedKey(wsUUID, true)].forEach((key) => {
      const feed = conversationFeeds.value[key];
      if (feed) {
        feed.order = feed.order.filter((uuid) => uuid !== audioFileUUID);
      }
    });
    if (audioFiles.value[wsUUID]) {
      delete audioFiles.value[wsUUID][audioFileUUID];
    }
  }

  // Call the fetchWorkspaces method directly when this store is loaded.
  // Exposed as workspacesReady so a caller that needs a resolved active
  // workspace (e.g. starting a recording) can await this SAME load instead
  // of firing a redundant concurrent fetchWorkspaces() call. fetchWorkspaces
  // swallows its own errors, so this never rejects.
  const workspacesReady = fetchWorkspaces();

  return {
    activeWorkspaceUUID,
    workspacesReady,
    setActiveWorkspaceUUID,
    setActiveWorkspace,
    workspaces,
    workspacesLoaded,
    fetchWorkspaces,
    addWorkspace,
    renameWorkspace,
    removeWorkspace,
    openPopupId,
    setOpenPopupId,
    audioFiles,
    audioFilesLoaded,
    activeAudioFile,
    playerCurrentTimeInMilliseconds,
    updatedPlayerTimeInMilliseconds,
    fileUploaded,
    fileDeleted,
    toggleFileUploaded,
    chatToken,
    isSubscribedState,
    subscriptionLoaded,
    subscriptionStatusSucceeded,
    fetchSubscriptionStatus,
    subscriptionSources,
    subscription,
    subscriptionSource,
    isYearly, // Expose the computed property so components can use it directly
    billingPortalUrl,
    billingPortalLoaded,

    // Daily file limit methods and properties
    hasExceededFileUploadLimit,
    remainingFiles,
    dailyFileCount,
    checkDailyFileUploadLimit,
    invalidateDailyFileLimit,

    // Audio file management methods
    fetchAudioFiles,
    fetchAudioFile,
    invalidateAudioFileCache,

    // Favorites methods
    toggleFavorite,
    getFavoriteAudioFiles,

    // Rename
    renameAudioFile,

    // Workspace policy + active-workspace lookups
    workspaceLimit,
    canCreateWorkspace,
    activeWorkspace,
    activeWorkspaceName,

    // Tag methods
    workspaceTags,
    fetchWorkspaceTags,
    attachTagToFile,
    detachTagFromFile,

    // Conversation feed (infinite scroll)
    conversationFeeds,
    getConversationFeed,
    loadConversationsFirstPage,
    loadConversationsNextPage,
    refreshConversationsFirstPage,
    removeAudioFileFromFeeds,

    // Conversation filters (tag + date)
    conversationFilters,
    getConversationFilters,
    setConversationFilters,
  };
});
